import _ from "lodash";
import {
  createMultiLevelNodesDraftMutation,
  deleteMultiLevelNodesDraftMutation,
  updateMultiLevelNodesDraftMutation,
  publishMultiLevelNodeDraftMutation,
  createMultiLevelTagsMutation,
  reorderMultiLevelTagsMutation,
  updateMultiLevelTagLabelMutation,
  deleteMultiLevelTagMutation,
} from "./NodeEditorMutations";
import client from "lib/apolloClient/client";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getMultiLevelNodesQuery } from "modules/CommonQuery";
import {
  getOrganizationMultiLevelNodeTagsFromCache,
  writeOrganizationMultiLevelNodeTagsToCache,
  writeOrganizationMultiLevelNodeTagToCache,
  getOrganizationMultiLevelNodeTagFromCache,
} from "modules/CommonGraphqlHelpers";
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import update from "immutability-helper";
import { generateRandomId } from "Utils";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";

export const createMultiLevelNodesDraft = ({
  input,
  inputForMultiLevelNodeQuery,
}) => {
  return async (dispatch, getState) => {
    try {
      const result = await client.mutate({
        mutation: createMultiLevelNodesDraftMutation,
        variables: input,
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getMultiLevelNodesQuery,
            variables: inputForMultiLevelNodeQuery,
          },
        ],
      });

      return _.get(
        result,
        "data.organizationManagement.createMultiLevelNodesDraft.draft.id",
        false
      );
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const deleteMultiLevelNodesDraft = params => {
  return async (dispatch, getState) => {
    try {
      const result = await client.mutate({
        mutation: deleteMultiLevelNodesDraftMutation,
        variables: params,
      });

      const isSuccess = _.get(
        result,
        "data.organizationManagement.deleteMultiLevelNodesDraft.isSuccess",
        false
      );
      if (!isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-bottom-left",
          })
        );
        return false;
      } else {
        return true;
      }
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const updateMultiLevelNodesDraft = params => {
  return async (dispatch, getState) => {
    try {
      const result = await client.mutate({
        mutation: updateMultiLevelNodesDraftMutation,
        variables: params,
      });

      const isSuccess = _.get(
        result,
        "data.organizationManagement.updateMultiLevelNodesDraft",
        false
      );
      if (!isSuccess) {
        return false;
      } else {
        return true;
      }
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const publishMultiLevelNodeDraft = ({
  draftId,
  plannerElementSetId,
  inputForMultiLevelNodeQuery,
}) => {
  return async dispatch => {
    try {
      const result = await client.mutate({
        mutation: publishMultiLevelNodeDraftMutation,
        variables: { draftId, plannerElementSetId },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getMultiLevelNodesQuery,
            variables: inputForMultiLevelNodeQuery,
          },
        ],
      });

      const isSuccess = _.get(
        result,
        "data.planner.publishMultiLevelNodeDraft",
        false
      );

      return isSuccess;
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const createMultiLevelTags = params => {
  return async (dispatch, getState) => {
    const { input, inputForMultiLevelTagsQuery } = params;
    const state = getState();

    const oldTagData = getOrganizationMultiLevelNodeTagsFromCache(
      inputForMultiLevelTagsQuery
    );
    const oldTagList = _.get(oldTagData, "genericTagsV2", []);
    const newTag = {
      id: generateRandomId(),
      label: input[0].label,
      displaySequence: _.get(_.last(oldTagList), "displaySequence", 0) + 1,
      icon: null,
      __typename: "GenericTag",
    };

    const updatedData = update(oldTagData, {
      genericTagsV2: {
        $push: [newTag],
      },
    });
    const updatedTagData = {
      node: updatedData,
    };

    const curriculumProgramId = getSelectedCurriculumId(state);

    writeOrganizationMultiLevelNodeTagsToCache({
      variables: inputForMultiLevelTagsQuery,
      data: updatedTagData,
    });
    try {
      const queryData = await client.mutate({
        mutation: createMultiLevelTagsMutation,
        variables: {
          input: {
            ...input,
            ...getCurriculumIdsFilterForTags({
              curriculumProgramId,
              isMutation: true,
            }),
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            insertGenericTagsV2: [newTag],
          },
        },
        update: (
          cache,
          {
            data: {
              platform: { insertGenericTagsV2 },
            },
          }
        ) => {
          const updatedDataFromResponse = update(oldTagData, {
            genericTagsV2: {
              $push: insertGenericTagsV2,
            },
          });
          const updatedTagDataFromResponse = {
            node: updatedDataFromResponse,
          };

          writeOrganizationMultiLevelNodeTagsToCache({
            variables: inputForMultiLevelTagsQuery,
            data: updatedTagDataFromResponse,
          });
        },
      });
      return _.get(queryData, "data.platform.insertGenericTagsV2[0]");
    } catch (e) {
      writeOrganizationMultiLevelNodeTagsToCache({
        variables: inputForMultiLevelTagsQuery,
        data: oldTagData,
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const reorderMultiLevelTags = params => {
  return async (dispatch, getState) => {
    try {
      const { id, putAfterItemId, inputForMultiLevelTagsQuery } = params;
      const result = await client.mutate({
        mutation: reorderMultiLevelTagsMutation,
        variables: { id, putAfterItemId },
      });
      const oldTagData = getOrganizationMultiLevelNodeTagsFromCache(
        inputForMultiLevelTagsQuery
      );
      const oldTagList = _.get(oldTagData, "genericTagsV2", []);
      const clonedOldTagList = _.cloneDeep(oldTagList);

      const draggedIndex = _.findIndex(oldTagList, { id });

      // Remove the dragged item from its original position
      const draggedItem = clonedOldTagList.splice(draggedIndex, 1)[0];

      // Find the index of the item after which the dragged item should be placed
      const putAfterIndex = _.findIndex(clonedOldTagList, {
        id: putAfterItemId,
      });

      // Insert the dragged item after the specified index
      clonedOldTagList.splice(putAfterIndex + 1, 0, draggedItem);

      // Update the displaySequence of the tags
      const updatedData = clonedOldTagList.map((tag, index) => {
        const oldTag = oldTagList.find(oldTag => oldTag.id === tag.id);
        return {
          ...oldTag,
          displaySequence: oldTagList[index].displaySequence,
        };
      });
      writeOrganizationMultiLevelNodeTagsToCache({
        variables: inputForMultiLevelTagsQuery,
        data: {
          node: {
            ...oldTagData,
            genericTagsV2: updatedData,
          },
        },
      });

      return _.get(result, "data.platform.reorderGenericTags", false);
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const updateMultiLevelTagLabel = params => {
  return async (dispatch, getState) => {
    try {
      const { input } = params;
      await client.mutate({
        mutation: updateMultiLevelTagLabelMutation,
        variables: { input },
      });

      const { id, label } = input;
      const oldTagDetails = getOrganizationMultiLevelNodeTagFromCache({ id });

      const updatedData = update(oldTagDetails, {
        label: { $set: label },
      });
      writeOrganizationMultiLevelNodeTagToCache({
        id,
        data: updatedData,
      });
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const deleteMultiLevelTag = params => {
  return async (dispatch, getState) => {
    try {
      const { input, inputForMultiLevelTagsQuery } = params;
      const result = await client.mutate({
        mutation: deleteMultiLevelTagMutation,
        variables: { input },
      });

      const { ids } = input;
      const id = _.first(ids);
      const oldTagData = getOrganizationMultiLevelNodeTagsFromCache(
        inputForMultiLevelTagsQuery
      );
      const oldTagList = _.get(oldTagData, "genericTagsV2", []);
      const updatedData = oldTagList.filter(tag => tag.id !== id);
      writeOrganizationMultiLevelNodeTagsToCache({
        variables: inputForMultiLevelTagsQuery,
        data: {
          node: {
            ...oldTagData,
            genericTagsV2: updatedData,
          },
        },
      });
      const isDeleted = _.get(result, "data.platform.deleteGenericTags", false);
      if (isDeleted) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:tag_deleted_successfully",
            type: "success",
            position: "toast-bottom-left",
          })
        );
      }
      return isDeleted;
    } catch (e) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};
